import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/services/api-client";
import { toast } from "sonner";
import { useState } from "react";
import PasswordStrengthBar from "react-password-strength-bar";
import { PasswordInput } from "@/components/ui/password-input";
import { FormError } from '@/components/ui/form-error';

const resetPasswordSchema = z
  .object({
    new_password: z
      .string()
      .min(8, "A senha deve ter no mínimo 8 caracteres"),
    re_new_password: z.string(),
  })
  .refine((data) => data.new_password === data.re_new_password, {
    message: "As senhas não coincidem",
    path: ["re_new_password"],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

interface ResetPasswordFormProps {
  mode?: 'reset' | 'invite';
}

export const ResetPasswordForm = ({ mode = 'reset' }: ResetPasswordFormProps) => {
  const navigate = useNavigate();
  const { token, uid } = useParams<{ token: string; uid: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState('');

  const copy = mode === 'invite'
    ? {
        title: 'Bem-vindo!',
        subtitle: 'Defina sua senha de acesso',
        submit: 'Definir senha',
        success: 'Senha definida com sucesso! Faça login para continuar.',
      }
    : {
        title: 'Recuperar Acesso',
        subtitle: 'Digite sua nova senha',
        submit: 'Recuperar acesso',
        success: 'Senha alterada com sucesso!',
      };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    setIsLoading(true);
    setError(undefined);
    try {
      if (!token || !uid) {
        throw new Error("Token or UID not found");
      }
      await apiClient.resetPasswordConfirm({ ...data, token, uid });
      toast.success(copy.success);
      navigate("/login");
    } catch {
      setError("Erro ao alterar a senha. Verifique o link e tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-[350px]">
      <CardHeader>
        <CardTitle>{copy.title}</CardTitle>
        <p className="text-sm">{copy.subtitle}</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleResetPassword)}>
          <div className="grid w-full items-center gap-4">
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="new_password">Nova senha</Label>
              <PasswordInput
                id="new_password"
                placeholder="Digite sua nova senha"
                {...register("new_password")}
                onChange={(e) => {
                  setPassword(e.target.value);
                  register("new_password").onChange(e);
                }}
              />
              <PasswordStrengthBar
                password={password}
                shortScoreWord="muito curta"
                scoreWords={['muito fraca', 'fraca', 'razoável', 'boa', 'forte']}
              />
              {errors.new_password && (
                <FormError message={errors.new_password.message} />
              )}
            </div>
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="re_new_password">Confirme sua nova senha</Label>
              <PasswordInput
                id="re_new_password"
                placeholder="Confirme sua nova senha"
                {...register("re_new_password")}
              />
              {errors.re_new_password && (
                <FormError message={errors.re_new_password.message} />
              )}
            </div>
            <FormError message={error} />
            <div className="flex justify-between items-center w-full mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/login")}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Aguarde..." : copy.submit}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
      </CardFooter>
    </Card>
  );
};